import { flatten, get, isNil } from 'lodash'
import { Optional } from 'utility-types'
import {
  PrefilledData,
  Question as QuestionInterface,
  QUESTION_TYPES,
  ValidationRules,
  FlattenQuestionSurveyProps
} from '@commutifi-fe/interfaces'
import { Form, FormInstance, MarkdownWrapper, Question } from '@commutifi-fe/ui'
import { QuestionProps } from '@commutifi/models/Question'
import { COMPONENT_TYPES, coreQuestionTypeRefKeyMap, deletableCoreQuestionTypeRefKeyMap } from 'constants/surveys'
import { AddressPoint, OfficeGeocodeValue } from 'shared/interfaces'
import { CumulatedAnswers } from 'shared/interfaces/Survey'
import { REF_KEYS_LIST } from 'constants/onboarding'

/**
 * Question type is specific to our survey that commuters take. Those questions have a
 * single localized content and have some specially processed data.
 *
 * @param question - the question object
 * @returns the question reference key or id (As we consider ref keys an id on the commuter survey)
 */
export const getQuestionId = (question: QuestionInterface) => question.referenceKey || question.id

export const getOfficeGeolocation = (
  prefilledData: PrefilledData = {},
  cumulatedAnswers: CumulatedAnswers = {}
): OfficeGeocodeValue | undefined => {
  const officeAnswer = cumulatedAnswers[REF_KEYS_LIST.accountOfficeId]

  /**
   * Priority to get building info:
   *   1. Prefilled info through enterpriseSurvey object
   *   2. User answer in survey (When updating his address this will be needed)
   *   3. If there is a current address context we use that only if needed (last case)
   */
  const accountContextOffice = prefilledData.accountContext?.office
  const buildingAnswer = prefilledData.building
    ? {
        formattedAddress: prefilledData.building.address,
        location: prefilledData.building.location,
        value: prefilledData.building.office?.id
      }
    : officeAnswer

  return (
    buildingAnswer ||
    (accountContextOffice
      ? {
          formattedAddress: accountContextOffice?.building.address,
          location: accountContextOffice?.building.location,
          value: accountContextOffice?.id
        }
      : undefined)
  )
}

export const getHomeGeolocation = (cumulatedAnswers: CumulatedAnswers = {}): AddressPoint | undefined =>
  cumulatedAnswers[REF_KEYS_LIST.accountAddress]

/**
 * antd field names are like this ['parentProp', 'nested', etc.]
 * This function is just doing that formatting from an id like this: 'parentProp.nested.subProp'
 * @param question - the question object
 * @param subQuestionId - the id of the sub question
 * @returns the formatted id
 */
export const generateSubQuestionId = (question: QuestionInterface, subQuestionId: string) => {
  if (!question?.id) {
    return ''
  }
  return flatten([getQuestionId(question), (subQuestionId || '').split('.')])
}

export const isQuestionBefore = (
  q1: Optional<Pick<QuestionInterface, 'section' | 'order'>>,
  q2: Optional<Pick<QuestionInterface, 'section' | 'order'>>,
  orEqual: boolean = false
) =>
  !isNil(q1.section) &&
  !isNil(q2.section) &&
  (q1.section < q2.section ||
    (q1.section === q2.section &&
      !isNil(q1.order) &&
      !isNil(q2.order) &&
      (orEqual ? q1.order <= q2.order : q1.order < q2.order)))

export const isQuestionPositionEqual = (
  q1: Optional<Pick<QuestionInterface, 'section' | 'order'>>,
  q2: Optional<Pick<QuestionInterface, 'section' | 'order'>>
) => !isNil(q1.section) && !isNil(q2.section) && q1.section === q2.section && q1.order === q2.order

/**
 * Check if a question is a core question based on its type and reference key. This was determined by the team
 * and can be found here although it's subject to changes: https://www.notion.so/commutifi/Survey-Building-bf5d72589a2a4fd29cee583cb95c11ff?pvs=4#175abf27a7458069a0fbda56dbc6f7df
 * @param question - the question object
 * @returns true if the question is a core question, false otherwise
 */
export const getIsCoreQuestion = (
  questionOrQuestionSurvey: QuestionInterface | QuestionProps | FlattenQuestionSurveyProps
): boolean => {
  if (!questionOrQuestionSurvey.type || !questionOrQuestionSurvey.referenceKey) {
    return false
  }

  const refKey = coreQuestionTypeRefKeyMap.get(questionOrQuestionSurvey.type as NonNullable<QuestionProps['type']>)
  return (
    !isNil(refKey) &&
    refKey.includes(questionOrQuestionSurvey.referenceKey as NonNullable<QuestionProps['referenceKey']>)
  )
}

export const getIsDeletableCoreQuestion = (
  questionOrQuestionSurvey: QuestionInterface | QuestionProps | FlattenQuestionSurveyProps
): boolean =>
  getIsCoreQuestion(questionOrQuestionSurvey) &&
  deletableCoreQuestionTypeRefKeyMap.has(questionOrQuestionSurvey.type as NonNullable<QuestionProps['type']>)

export const getIsDeletableQuestion = (
  questionOrQuestionSurvey: QuestionInterface | QuestionProps | FlattenQuestionSurveyProps
): boolean => getIsDeletableCoreQuestion(questionOrQuestionSurvey) || !getIsCoreQuestion(questionOrQuestionSurvey)

export const renderQuestion = (questionProps: {
  validation?: ValidationRules
  question: QuestionInterface | FlattenQuestionSurveyProps
  fieldName: string | string[]
  props: Partial<any>
  component: React.ComponentType<any>
  componentType: COMPONENT_TYPES
  defaultValue?: any
  form?: FormInstance
  upsertQuestions?: (questions: QuestionInterface[]) => void
}) => {
  const {
    question,
    validation,
    fieldName,
    props,
    component: Component,
    componentType,
    form,
    defaultValue,
    upsertQuestions
  } = questionProps

  return componentType === COMPONENT_TYPES.Module ? (
    <Component
      key={question.id}
      question={question}
      form={form}
      upsertQuestions={upsertQuestions}
      validation={validation}
      defaultValue={defaultValue}
      {...props}
    />
  ) : question.type === QUESTION_TYPES.statement ? (
    <Question
      key={question.id}
      title={
        typeof get(question, 'content.title') === 'string' ? (
          <MarkdownWrapper source={get(question, 'content.title', '') as string} />
        ) : (
          get(question, 'content.title')
        )
      }
      hint={
        typeof get(question, 'content.hint') === 'string' ? (
          <MarkdownWrapper source={get(question, 'content.hint', '')} />
        ) : (
          get(question, 'content.hint')
        )
      }
      data-testid={fieldName}
    />
  ) : (
    <Form.Item
      name={fieldName}
      key={question.id}
      data-testid={fieldName}
      label={<Question title={get(question, 'content.title')} hint={get(question, 'content.hint')} />}
      required={!question.optional}
      initialValue={defaultValue}
      {...validation}
    >
      <Component {...props} form={form} />
    </Form.Item>
  )
}
